import { DOB_VALIDATION, PII_ERROR_KEYS, SSN_VALIDATION } from './constants';

/**
 * Validates SSN format and returns error message key if invalid
 * @param {string} ssnValue - The SSN value to validate
 * @returns {string|null} - Error message key if invalid, null if valid
 */
export const validateSsn = (ssnValue) => {
  // Remove any non-digit characters
  const cleanSsn = ssnValue.replace(/\D/g, '');

  // Check if it's 9 digits
  if (cleanSsn.length !== SSN_VALIDATION.REQUIRED_LENGTH) {
    return PII_ERROR_KEYS.SSN_MUST_BE_9_DIGITS;
  }

  // Check for invalid SSN patterns
  const hasInvalidPattern = SSN_VALIDATION.INVALID_PATTERNS.some((pattern) =>
    pattern.test(cleanSsn)
  );
  if (hasInvalidPattern) {
    return PII_ERROR_KEYS.INVALID_SSN_FORMAT;
  }

  return null;
};

/**
 * Formats SSN value with dashes (XXX-XX-XXXX)
 * @param {string} value - The SSN value to format
 * @returns {string} - Formatted SSN string
 */
export const formatSsn = (value) => {
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 3) return cleanValue;
  if (cleanValue.length <= 5) return `${cleanValue.slice(0, 3)}-${cleanValue.slice(3)}`;
  return `${cleanValue.slice(0, 3)}-${cleanValue.slice(3, 5)}-${cleanValue.slice(5, 9)}`;
};

/**
 * Validates date of birth and returns error message key if invalid
 * @param {Date} date - The date to validate
 * @returns {string|null} - Error message key if invalid, null if valid
 */
export const validateDob = (date) => {
  if (!date) return PII_ERROR_KEYS.INVALID_DOB;

  // Check if it's a valid Date object
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return PII_ERROR_KEYS.INVALID_DOB;
  }

  // Check if date is within reasonable bounds (not too far in the past or future)
  const today = DOB_VALIDATION.getMaxDate();
  const minDate = new Date(DOB_VALIDATION.MIN_DATE);

  if (date < minDate) {
    return PII_ERROR_KEYS.DOB_TOO_OLD;
  }

  // Check if the date is not in the future (using single consistent date check)
  if (date > today) {
    return PII_ERROR_KEYS.DOB_IN_FUTURE;
  }

  return null;
};

/**
 * Checks if SSN save button should be disabled
 * @param {boolean} isEditMode - Whether the modal is in edit mode
 * @param {boolean} isLoading - Whether a request is in progress
 * @param {string} ssn - The SSN value
 * @param {string} verifySsn - The verify SSN value
 * @param {string} ssnError - SSN validation error
 * @param {string} verifySsnError - Verify SSN validation error
 * @returns {boolean} - True if save should be disabled
 */
export const isSsnSaveDisabled = (
  isEditMode,
  isLoading,
  ssn,
  verifySsn,
  ssnError,
  verifySsnError
) => {
  if (!isEditMode) return true;
  if (isLoading) return true;
  if (!ssn || !verifySsn) return true;
  if (ssnError || verifySsnError) return true;

  // Clean both values to digits-only for comparison
  const cleanSsn = ssn.replace(/\D/g, '');
  const cleanVerifySsn = verifySsn.replace(/\D/g, '');
  if (cleanSsn !== cleanVerifySsn) return true;

  return false;
};

/**
 * Validates SSN verification match
 * @param {string} ssn - The original SSN
 * @param {string} verifySsn - The verification SSN
 * @returns {string|null} - Error message key if they don't match, null if they match
 */
export const validateSsnMatch = (ssn, verifySsn) => {
  if (verifySsn && ssn) {
    // Clean both values to digits-only for comparison
    const cleanSsn = ssn.replace(/\D/g, '');
    const cleanVerifySsn = verifySsn.replace(/\D/g, '');

    if (cleanVerifySsn !== cleanSsn) {
      return PII_ERROR_KEYS.SSN_DOES_NOT_MATCH;
    }
  }
  return null;
};

/**
 * Cleans SSN by removing formatting and returns as string
 * @param {string} ssn - The formatted SSN string
 * @returns {string} - The SSN as a clean string (digits only, preserving leading zeros)
 */
export const cleanSsnForApi = (ssn) => {
  const cleanSsn = ssn.replace(/\D/g, '');
  return cleanSsn;
};
